import type { OntologyEntry } from '../types.js';

/**
 * KAN-822 — `vendor-update-subtype`, the commercial-content subtypes of the
 * Vendor updates stream.
 *
 * This dimension is ORTHOGONAL to `content-type` and `content-section`, and
 * deliberately does not replace either. At the time it was added, `content-type`
 * already held `vendor-intel`, `industry-news`, `report`, `research-report` and
 * `whitepaper`, and `content-section` already held `vendor-intelligence` — but
 * none of those expresses the three approved commercial subtypes without
 * conflating original research with promotion, which is precisely the
 * distinction the feature turns on. A product announcement may still carry
 * `vendor-intel`; company news may still carry `industry-news`; a promotional
 * report may still carry `report` or `whitepaper`. Those are compatible
 * contextual pairings, not classification rules: what makes a story a Vendor
 * update is its distribution class plus verified commercial intent, never its
 * publisher and never its topical section.
 *
 * Registering here rather than in a feature-local union is the whole point.
 * API validation, the migration's CHECK and any UI label are DERIVED from this
 * entry set, so a fourth subtype is one edit, and no second curated taxonomy
 * can drift away from this one.
 */
export const vendorUpdateSubtypes: OntologyEntry[] = [
  {
    slug: 'product-announcement',
    name: 'Product Announcement',
    description: 'A vendor announcing a product, release, feature or capability.',
    dimension: 'vendor-update-subtype',
    sortOrder: 0,
  },
  {
    slug: 'company-news',
    name: 'Company News',
    description:
      'Corporate news about a vendor: funding, acquisition, leadership, partnerships, results.',
    dimension: 'vendor-update-subtype',
    sortOrder: 1,
  },
  {
    slug: 'promotional-report',
    name: 'Promotional Report',
    description:
      'Vendor-published reporting whose primary purpose is promotion rather than original research.',
    dimension: 'vendor-update-subtype',
    sortOrder: 2,
  },
];
